import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  FileHandlerDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const ELLIPSIS_STYLES = {
  overflow: 'hidden',
  'text-overflow': 'ellipsis',
  'white-space': 'nowrap',
} as const;

const ROW_HEIGHT = '24px';
const METADATA_MIN_HEIGHT = '16px';

export const getFileHandlerTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): FileHandlerDesignTokens => {
  const { spacing, typography, tokens } = theme;
  const { colors } = tokens;
  const { bodyMedium, caption } = typography;

  return {
    container: {
      'padding-top': px(spacing.S),
      'padding-bottom': px(spacing.S),
      'padding-left': px(spacing.M),
      'padding-right': px(spacing.M),
      'box-shadow': `0px 1px 0px 0px ${colors.border.subtle.value}`,
      flex: '1',
      display: 'flex',
    },
    'content-container': {
      ...ELLIPSIS_STYLES,
      flex: '1',
    },
    icon: {
      'margin-right': px(spacing.XS),
      'flex-shrink': 0,
      disabled: {
        true: { 'icon-color': colors.text.inactive.value, opacity: undefined },
        false: { 'icon-color': colors.text.primary.value, opacity: undefined },
      },
    },
    action: {
      base: {
        height: ROW_HEIGHT,
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
        'flex-shrink': '0',
      },
      main: { 'margin-left': px(spacing.M) },
      status: { 'margin-left': px(spacing.XS) },
      secondary: { 'margin-left': px(spacing.M) },
    },
    name: {
      _: {
        'font-family': bodyMedium.fontFamily,
        'font-size': bodyMedium.fontSize,
        'font-weight': bodyMedium.fontWeight,
        'line-height': bodyMedium.lineHeight,
        height: ROW_HEIGHT,
        display: 'flex',
        'align-items': 'center',
      },
      disabled: {
        true: { color: colors.text.inactive.value, opacity: undefined },
        false: { color: colors.text.primary.value, opacity: undefined },
      },
      removed: {
        true: { 'text-decoration': 'line-through' },
        false: { 'text-decoration': 'none' },
      },
    },
    metadata: {
      ...ELLIPSIS_STYLES,
      'font-family': caption.fontFamily,
      'font-size': caption.fontSize,
      'font-weight': caption.fontWeight,
      'line-height': caption.lineHeight,
      color: colors.text.tertiary.value,
      'min-height': METADATA_MIN_HEIGHT,
    },
    'status-icon': {
      success: { 'icon-color': colors.icon.success.value },
      error: { 'icon-color': colors.icon.error.value },
      warning: { 'icon-color': colors.icon.warning.value },
    },
    'filename-tooltip': {
      ...ELLIPSIS_STYLES,
      'min-width': '0',
    },
  };
};

export const getFileHandlerTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): FileHandlerDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const statusColor = semantic?.['status-color'];

  const spacing8 = generic?.spacing?.[8];
  const spacing12 = generic?.spacing?.[12];
  const spacing16 = generic?.spacing?.[16];
  const rowHeight = generic?.size?.[24];
  const textDefault = semantic?.['text-color']?.medium?.default;
  const textLight = semantic?.['text-color']?.light;
  const dividerColor = semantic?.['border-color']?.divider?.light;
  const disabledOpacity = semantic?.opacity?.disabled;

  return {
    container: {
      'padding-top': spacing12,
      'padding-bottom': spacing12,
      'padding-left': spacing16,
      'padding-right': spacing16,
      'box-shadow': dividerColor
        ? `0px 1px 0px 0px ${dividerColor}`
        : undefined,
      flex: '1',
      display: 'flex',
    },
    'content-container': {
      ...ELLIPSIS_STYLES,
      flex: '1',
    },
    icon: {
      'margin-right': spacing8,
      'flex-shrink': 0,
      disabled: {
        true: { 'icon-color': textDefault, opacity: disabledOpacity },
        false: { 'icon-color': textDefault, opacity: undefined },
      },
    },
    action: {
      base: {
        height: rowHeight,
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
        'flex-shrink': '0',
      },
      main: { 'margin-left': spacing16 },
      status: { 'margin-left': spacing8 },
      secondary: { 'margin-left': spacing16 },
    },
    name: {
      _: {
        'font-family': fontFamily,
        'font-size': generic?.['font-size']?.[14],
        'font-weight': '400',
        'line-height': generic?.['line-height']?.[20],
        height: rowHeight,
        display: 'flex',
        'align-items': 'center',
      },
      disabled: {
        true: { color: textDefault, opacity: disabledOpacity },
        false: { color: textDefault, opacity: undefined },
      },
      removed: {
        true: { 'text-decoration': 'line-through' },
        false: { 'text-decoration': 'none' },
      },
    },
    metadata: {
      ...ELLIPSIS_STYLES,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[12],
      'font-weight': '400',
      'line-height': generic?.['line-height']?.[16],
      color: textLight,
      'min-height': generic?.size?.[16],
    },
    'status-icon': {
      success: { 'icon-color': statusColor?.success },
      error: { 'icon-color': statusColor?.error },
      warning: { 'icon-color': statusColor?.warning },
    },
    'filename-tooltip': {
      ...ELLIPSIS_STYLES,
      'min-width': '0',
    },
  };
};
